import type { ApiClient } from '../client';
import type {
  ApiResponse,
  ClassSummary,
  CreateAcademicProgramRequest,
  CreateAcademicProgramResponse,
  CreateClassRequest,
  CreateClassResponse,
  CreateSectionRequest,
  CreateSectionResponse,
  ListAcademicProgramsResponse,
  ListClassesParams,
  ListClassesResponse,
  ListSectionsResponse,
  SoftDeleteClassRequest,
  UpdateClassRequest,
  UpdateSectionRequest,
} from '../types';
import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery, collectAllPages } from '../../../types/pagination';

export function bindClasses(client: ApiClient) {
  return {
async listClasses(params?: ListClassesParams): Promise<ApiResponse<ListClassesResponse>> {
    const qs = new URLSearchParams();
    if (params?.program_id) qs.set('program_id', params.program_id);
    if (params?.grade_level != null) qs.set('grade_level', String(params.grade_level));
    if (params?.semester != null) qs.set('semester', String(params.semester));
    appendPaginationQuery(qs, params);
    const query = qs.toString();
    return client.request<ListClassesResponse>(query ? `/classes?${query}` : '/classes');
  },

  async listAllClasses(params?: Omit<ListClassesParams, 'page' | 'limit' | 'offset'>): Promise<ListClassesResponse['classes']> {
    return collectAllPages(async (page, limit) => {
      const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (params?.program_id) qs.set('program_id', params.program_id);
      if (params?.grade_level != null) qs.set('grade_level', String(params.grade_level));
      if (params?.semester != null) qs.set('semester', String(params.semester));
      const res = await client.request<ListClassesResponse>(`/classes?${qs}`);
      return {
        items: res.data?.classes ?? [],
        totalPages: res.data?.total_pages,
      };
    });
  },

async listAcademicPrograms(): Promise<ApiResponse<ListAcademicProgramsResponse>> {
    return client.request<ListAcademicProgramsResponse>('/academic-programs');
  },

async createAcademicProgram(
    body: CreateAcademicProgramRequest
  ): Promise<ApiResponse<CreateAcademicProgramResponse>> {
    return client.request<CreateAcademicProgramResponse>('/academic-programs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async listSections(params?: PaginationQuery): Promise<ApiResponse<ListSectionsResponse>> {
    const qs = new URLSearchParams();
    appendPaginationQuery(qs, params);
    const query = qs.toString();
    return client.request<ListSectionsResponse>(query ? `/sections?${query}` : '/sections');
  },

  async listAllSections(): Promise<ListSectionsResponse['sections']> {
    return collectAllPages(async (page, limit) => {
      const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
      const res = await client.request<ListSectionsResponse>(`/sections?${qs}`);
      return {
        items: res.data?.sections ?? [],
        totalPages: res.data?.total_pages,
      };
    });
  },

async createSection(body: CreateSectionRequest): Promise<ApiResponse<CreateSectionResponse>> {
    return client.request<CreateSectionResponse>('/sections', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async updateSection(body: UpdateSectionRequest): Promise<ApiResponse<void>> {
    return client.request<void>('/sections/update', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async getClassById(classId: string): Promise<ApiResponse<ClassSummary | null>> {
    const res = await this.listClasses();
    const list = res.data?.classes ?? [];
    const found = list.find((c) => c.id === classId) ?? null;
    return { ...res, data: found };
  },

async createClass(body: CreateClassRequest): Promise<ApiResponse<CreateClassResponse>> {
    return client.request<CreateClassResponse>('/classes', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async updateClass(body: UpdateClassRequest): Promise<ApiResponse<void>> {
    return client.request<void>('/classes/update', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async softDeleteClass(body: SoftDeleteClassRequest): Promise<ApiResponse<void>> {
    return client.request<void>('/classes/delete', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  };
}
